import {
  collection, addDoc, query, where, orderBy, limit as firestoreLimit,
  startAfter, getDocs, onSnapshot, Timestamp, DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { ActivityLog, ActivityLogFilters, ActivityAction, ActivityEntityType } from '../types/activityLog.types';

const COLLECTION = 'activityLogs';

interface LogActivityParams {
  userId: string;
  userName: string;
  userRole: string;
  organizationId?: string;
  action: ActivityAction;
  entityType: ActivityEntityType;
  entityId?: string;
  entityName?: string;
  projectId?: string;
  projectName?: string;
  details?: Record<string, any>;
}

/**
 * Fire-and-forget activity log write. Never throws.
 */
export async function logActivity(params: LogActivityParams): Promise<void> {
  try {
    await addDoc(collection(db, COLLECTION), {
      ...params,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Failed to log activity:', error);
  }
}

/**
 * Paginated query for activity logs.
 */
export async function getActivityLogs(
  filters: ActivityLogFilters,
  pageSize: number = 50,
  lastDoc?: DocumentSnapshot
): Promise<{ logs: ActivityLog[]; lastDoc: DocumentSnapshot | null; hasMore: boolean }> {
  const constraints: any[] = [];

  if (filters.userId) {
    constraints.push(where('userId', '==', filters.userId));
  }
  if (filters.action) {
    constraints.push(where('action', '==', filters.action));
  }
  if (filters.entityType) {
    constraints.push(where('entityType', '==', filters.entityType));
  }
  if (filters.projectId) {
    constraints.push(where('projectId', '==', filters.projectId));
  }
  if (filters.startDate) {
    constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.startDate)));
  }
  if (filters.endDate) {
    constraints.push(where('createdAt', '<=', Timestamp.fromDate(filters.endDate)));
  }

  constraints.push(orderBy('createdAt', 'desc'));

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  constraints.push(firestoreLimit(pageSize));

  const q = query(collection(db, COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  const logs = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as ActivityLog));

  const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

  return {
    logs,
    lastDoc: newLastDoc,
    hasMore: snapshot.docs.length === pageSize,
  };
}

/**
 * Real-time listener for recent activity.
 */
export function subscribeToRecentActivity(
  filters: ActivityLogFilters,
  callback: (logs: ActivityLog[]) => void,
  maxItems: number = 20
): () => void {
  const constraints: any[] = [];

  if (filters.userId) {
    constraints.push(where('userId', '==', filters.userId));
  }
  if (filters.projectId) {
    constraints.push(where('projectId', '==', filters.projectId));
  }

  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(firestoreLimit(maxItems));

  const q = query(collection(db, COLLECTION), ...constraints);

  return onSnapshot(q, (snapshot) => {
    const logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as ActivityLog));
    callback(logs);
  });
}
